import I18n from 'I18n';
import allSettled from 'framework-asset-preview-lib/internal/shared/utils/allSettled';
import { getPropertyValue } from 'framework-asset-preview-lib/internal/shared/utils/crmObject';
import { OBJECT_TYPE } from 'framework-asset-preview-lib/internal/shared/constants/objectTypes';
import { safeParseJSON } from 'framework-asset-preview-lib/internal/shared/utils/json';
import { capitalizeFirstLetter } from 'framework-asset-preview-lib/internal/shared/utils/assetPreview';
import FormDesignColor from './components/FormDesignColor';
import { fetchFormFields, fetchFormPerformance } from './api';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * From https://git.hubteam.com/HubSpot/IdentityBase/blob/873bf34dcebc046428c4fd2ea30b07e03ac37d0c/src/main/protobuf/contacts.proto#L411
 *
 * Used to translate from hs_form_type in the CRM
 */
const FORM_TYPE = {
  HUBSPOT: 0,
  FLOW: 1,
  CAPTURED: 2,
  FACEBOOK_LEAD_AD: 3,
  BLOG_COMMENT: 4,
  MEETING: 5,
  /**
   * @deprecated survey should be used for future feedback related forms
   */
  FEEDBACK: 6,
  TICKET_FORM: 7,
  DOCUMENT: 8,
  VIDEO_FORM: 9,
  QUESTIONNAIRE: 10,
  SURVEY: 11,
  PAYMENT: 12
};
export function getNonCrmPropertyValue(propertyName, nonCrmObject) {
  if (!nonCrmObject) {
    return undefined;
  }
  const formResponse = nonCrmObject;
  if (propertyName in formResponse) {
    return formResponse[propertyName];
  }
  return undefined;
}
export function getFormType(crmObject) {
  const hsFormType = getPropertyValue(crmObject, 'hs_form_type');
  return hsFormType && hsFormType in FORM_TYPE ? FORM_TYPE[hsFormType] : undefined;
}
export function getFormPerformanceStats({
  crmObject,
  nonCrmObject
}) {
  return allSettled([fetchFormPerformance(crmObject), fetchFormFields(crmObject)]).then(promises => {
    const [formPerformanceResult, fieldsResult] = promises;
    const defaultFormResponse = {
      totals: {
        formViews: 0,
        submissions: 0,
        submissionsPerFormView: 0
      }
    };
    const formPerformanceResponse = formPerformanceResult.status === 'fulfilled' ? Object.assign({}, defaultFormResponse, formPerformanceResult.value, {
      totals: Object.assign({}, formPerformanceResult.value.totals || defaultFormResponse.totals)
    }) : defaultFormResponse;
    const formFieldsResponse = fieldsResult.status === 'fulfilled' ? fieldsResult.value : {
      fields: []
    };
    const overviewPerfGroup = {
      stats: [{
        formatStyle: 'decimal',
        propertyName: 'hs_form_views',
        value: formPerformanceResponse.totals.formViews
      }, {
        formatStyle: 'decimal',
        propertyName: 'hs_form_submissions',
        value: formPerformanceResponse.totals.submissions
      }],
      title: I18n.text('frameworkAssetPreviewLib.tabs.performance.performanceGroup.0-28.overview')
    };
    const formFieldsPerfGroup = {
      stats: [{
        formatStyle: 'decimal',
        propertyName: 'hs_form_total_fields',
        value: formFieldsResponse.fields.length
      }, {
        formatStyle: 'decimal',
        propertyName: 'hs_form_contact_properties',
        value: formFieldsResponse.fields.filter(field => field.objectTypeId === OBJECT_TYPE.CONTACT).length
      }, {
        formatStyle: 'decimal',
        propertyName: 'hs_form_company_properties',
        value: formFieldsResponse.fields.filter(field => field.objectTypeId === OBJECT_TYPE.COMPANY).length
      }, {
        formatStyle: 'decimal',
        propertyName: 'hs_form_deal_properties',
        value: formFieldsResponse.fields.filter(field => field.objectTypeId === OBJECT_TYPE.DEAL).length
      }, {
        formatStyle: 'decimal',
        propertyName: 'hs_form_ticket_properties',
        value: formFieldsResponse.fields.filter(field => field.objectTypeId === OBJECT_TYPE.TICKET).length
      }, {
        formatStyle: 'decimal',
        propertyName: 'hs_form_custom_object_properties',
        value: formFieldsResponse.fields.filter(field => field.objectTypeId.startsWith('0-x')).length
      }],
      title: I18n.text('frameworkAssetPreviewLib.tabs.performance.performanceGroup.0-28.analyzeFormFields')
    };
    const formFieldGroups = getNonCrmPropertyValue('formFieldGroups', nonCrmObject) || [];
    const formFieldsArray = formFieldGroups.reduce( /* eslint-disable-next-line hubspot-dev/no-reduce-accumulator-copy */
    (acc, {
      fields
    }) => [...acc, fields], []).flat();
    const formSmartFieldsCount = formFieldsArray.filter(field => field.isSmartField).length;
    const formDependentFieldFiltersCount = formFieldsArray.filter(field => field.dependentFieldFilters.length).length;
    const formSettingsPerfGroup = {
      stats: [{
        formatStyle: 'text',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-28.hs_form_captcha_enabled'),
        propertyName: 'captchaEnabled',
        value: I18n.text(getNonCrmPropertyValue('captchaEnabled', nonCrmObject) ? 'frameworkAssetPreviewLib.propertyValue.0-28.formSettings.isOn' : 'frameworkAssetPreviewLib.propertyValue.0-28.formSettings.isOff')
      }, {
        formatStyle: 'text',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-28.hs_form_follow_up_id'),
        propertyName: 'followUpId',
        value: I18n.text(getNonCrmPropertyValue('followUpId', nonCrmObject) === 'true' ? 'frameworkAssetPreviewLib.propertyValue.0-28.formSettings.isOn' : 'frameworkAssetPreviewLib.propertyValue.0-28.formSettings.isOff')
      }, {
        formatStyle: 'text',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-28.hs_form_inline_message'),
        propertyName: 'inlineMessage',
        value: String(getNonCrmPropertyValue('inlineMessage', nonCrmObject) || '')
      }, {
        formatStyle: 'text',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-28.hs_form_redirect'),
        propertyName: 'redirect',
        value: String(getNonCrmPropertyValue('redirect', nonCrmObject) || '')
      }, {
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-28.hs_form_smart_fields_count'),
        formatStyle: 'text',
        propertyName: 'formSmartFieldsCount',
        value: formSmartFieldsCount
      }, {
        formatStyle: 'text',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-28.hs_form_dependent_field_filters_count'),
        propertyName: 'formDependentFieldFiltersCount',
        value: formDependentFieldFiltersCount
      }],
      title: I18n.text('frameworkAssetPreviewLib.tabs.performance.performanceGroup.0-28.formSettings')
    };
    const formDesign = safeParseJSON(String(getNonCrmPropertyValue('style', nonCrmObject) || '{}'), {
      fontFamily: '',
      helpTextColor: '',
      labelTextColor: '',
      legalConsentTextColor: '',
      submitAlignment: '',
      submitColor: ''
    });
    const formDesignPerfGroup = {
      stats: [{
        formatStyle: 'text',
        label: I18n.text('frameworkAssetPreviewLib.propertyLabel.0-28.hs_form_theme_name'),
        propertyName: 'themeName',
        value: I18n.text(`frameworkAssetPreviewLib.propertyValue.0-28.formTheme.${String(getNonCrmPropertyValue('themeName', nonCrmObject) || '')}`)
      }, {
        formatStyle: 'text',
        propertyName: 'hs_form_font_family',
        value: formDesign.fontFamily ? capitalizeFirstLetter(formDesign.fontFamily) : '--'
      }, {
        formatStyle: 'text',
        propertyName: 'hs_form_label_color',
        value: /*#__PURE__*/_jsx(FormDesignColor, {
          color: formDesign.labelTextColor
        })
      }, {
        formatStyle: 'text',
        propertyName: 'hs_form_help_color',
        value: /*#__PURE__*/_jsx(FormDesignColor, {
          color: formDesign.helpTextColor
        })
      }, {
        formatStyle: 'text',
        propertyName: 'hs_form_gdpr_description_color',
        value: /*#__PURE__*/_jsx(FormDesignColor, {
          color: formDesign.legalConsentTextColor
        })
      }, {
        formatStyle: 'text',
        propertyName: 'hs_form_button_color',
        value: /*#__PURE__*/_jsx(FormDesignColor, {
          color: formDesign.submitColor
        })
      }, {
        formatStyle: 'text',
        propertyName: 'hs_form_button_alignment',
        value: formDesign.submitAlignment ? I18n.text(`frameworkAssetPreviewLib.propertyValue.0-28.formAlignment.${formDesign.submitAlignment}`) : '--'
      }],
      title: I18n.text('frameworkAssetPreviewLib.tabs.performance.performanceGroup.0-28.formDesign')
    };
    return [overviewPerfGroup, formFieldsPerfGroup, formSettingsPerfGroup, formDesignPerfGroup];
  });
}